import { CREATIVE_JOB_TYPES } from "./errors";

const MINUTE_MS = 60 * 1000;
const SECOND_MS = 1000;

const DURATION_2_30_MS = 2.5 * MINUTE_MS;
const DURATION_3_MIN_MS = 3 * MINUTE_MS;
const DURATION_4_MIN_MS = 4 * MINUTE_MS;
const DURATION_7_MIN_MS = 7 * MINUTE_MS;
const DURATION_15_MIN_MS = 15 * MINUTE_MS;

const BATCH_IMAGES_PER_CHUNK = 5;

const TASK_ESTIMATED_DURATION_MS = {
  [CREATIVE_JOB_TYPES.generateImages]: DURATION_2_30_MS,
  [CREATIVE_JOB_TYPES.editImage]: DURATION_2_30_MS,
  [CREATIVE_JOB_TYPES.resizeImage]: DURATION_2_30_MS,
  [CREATIVE_JOB_TYPES.animateImage]: DURATION_3_MIN_MS,
  [CREATIVE_JOB_TYPES.generateVideo]: DURATION_15_MIN_MS,
  [CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform]: DURATION_4_MIN_MS,
  [CREATIVE_JOB_TYPES.generateCustomReport]: DURATION_7_MIN_MS,
  [CREATIVE_JOB_TYPES.analyzeImageLayers]: DURATION_2_30_MS,
  [CREATIVE_JOB_TYPES.analyzeCreativeTemplate]: DURATION_2_30_MS,
};

const getBatchDurationMs = ({ itemCount = 0, perChunkMs }) => {
  const normalizedCount = Math.max(0, Number(itemCount) || 0);
  if (normalizedCount <= 0) {
    return perChunkMs;
  }

  const chunks = Math.ceil(normalizedCount / BATCH_IMAGES_PER_CHUNK);
  return chunks * perChunkMs;
};

const getEditImagesBatchDurationMs = (itemCount = 0) =>
  getBatchDurationMs({ itemCount, perChunkMs: DURATION_2_30_MS });

/**
 * Returns estimated task duration in milliseconds for UI countdown and loaders.
 */
export const getTaskEstimatedDurationMs = ({ jobType, itemCount } = {}) => {
  if (jobType === CREATIVE_JOB_TYPES.editImagesBatch) {
    return getEditImagesBatchDurationMs(itemCount);
  }

  if (jobType === CREATIVE_JOB_TYPES.resizeImagesBatch) {
    return getBatchDurationMs({ itemCount, perChunkMs: DURATION_3_MIN_MS });
  }

  if (jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate) {
    return getBatchDurationMs({ itemCount, perChunkMs: DURATION_3_MIN_MS });
  }

  return TASK_ESTIMATED_DURATION_MS[jobType] ?? DURATION_2_30_MS;
};

/**
 * Formats remaining or total duration as mm:ss (e.g. "15:00", "2:30").
 */
export const formatTaskEstimatedDuration = ({ remainingMs } = {}) => {
  const normalizedMs = Math.max(0, Number(remainingMs) || 0);
  const totalSeconds = Math.ceil(normalizedMs / SECOND_MS);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Computes remaining time from persisted startedAt and estimated duration.
 */
export const getTaskRemainingDurationMs = ({
  startedAt,
  estimatedDurationMs,
  now = Date.now(),
} = {}) => {
  const duration = Number(estimatedDurationMs) || 0;
  const start = Number(startedAt) || 0;

  if (!duration || !start) {
    return duration;
  }

  const elapsed = Math.max(0, now - start);
  return Math.max(0, duration - elapsed);
};
